import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import {
    createLauraSAIProfile,
    listLauraSAIProfiles,
    updateLauraSAIProfile,
    uploadStandaloneDocument,
} from "@/app/lib/auditflowApi";
import type { LauraSAIProfile } from "@/app/components/shared/types";

const SAI_SLOTS = [
    { id: "r1_audit_manual", label: "R1 — Audit Manual / Methodology" },
    { id: "r2_standards", label: "R2 — Audit Standards" },
    { id: "r3_report_template", label: "R3 — Report Template" },
    { id: "r4_proposals_framework", label: "R4 — Proposals Framework" },
    { id: "r5_legal_framework", label: "R5 — Legal / Institutional Framework" },
];

export function LauraSAISettings() {
    const [profiles, setProfiles] = useState<LauraSAIProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const p = await listLauraSAIProfiles();
            setProfiles(p);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, []);

    const customProfiles = profiles.filter((p) => !p.is_builtin);
    const selected = profiles.find((p) => p.id === selectedId);

    async function handleCreate() {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            const p = await createLauraSAIProfile({
                name: newName.trim(),
                default_language: "en",
            });
            setNewName("");
            await load();
            setSelectedId(p.id);
        } finally {
            setCreating(false);
        }
    }

    async function handleUpload(slot: string, file: File) {
        if (!selected || selected.is_builtin) return;
        const doc = await uploadStandaloneDocument(file);
        await updateLauraSAIProfile(selected.id, {
            documents: [{ slot, document_id: doc.id }],
        });
        await load();
    }

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div>
                <h2 className="text-xl font-medium">LAURA — SAI Profiles</h2>
                <p className="mt-1 text-sm text-gray-500">
                    Built-in profiles (TCU, INTOSAI Generic) are read-only. Create custom
                    profiles for other Supreme Audit Institutions using Deployment Kit
                    document slots (R1–R5).
                </p>
            </div>

            <div className="rounded-lg border p-4">
                <h3 className="text-sm font-medium text-gray-900">Built-in profiles</h3>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                    {profiles
                        .filter((p) => p.is_builtin)
                        .map((p) => (
                            <li key={p.id}>
                                {p.name} ({p.default_language})
                            </li>
                        ))}
                </ul>
            </div>

            <div className="space-y-4">
                <h3 className="text-sm font-medium">Custom SAI profiles</h3>
                <div className="flex gap-2">
                    <input
                        className="flex-1 rounded-lg border px-3 py-2 text-sm"
                        placeholder="Profile name (e.g. My SAI)"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={creating || !newName.trim()}
                        className="flex items-center gap-1 rounded-lg bg-gray-900 px-4 py-2 text-sm text-white disabled:opacity-50"
                    >
                        {creating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Plus className="h-4 w-4" />
                        )}
                        Create
                    </button>
                </div>

                {customProfiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {customProfiles.map((p) => (
                            <button
                                key={p.id}
                                type="button"
                                onClick={() => setSelectedId(p.id)}
                                className={`rounded-lg border px-3 py-1.5 text-sm ${
                                    selectedId === p.id
                                        ? "border-gray-900 bg-gray-900 text-white"
                                        : "hover:bg-gray-50"
                                }`}
                            >
                                {p.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {selected && !selected.is_builtin && (
                <div className="rounded-lg border p-4 space-y-4">
                    <h3 className="font-medium">{selected.name}</h3>
                    {SAI_SLOTS.map((slot) => {
                        const doc = selected.laura_sai_profile_documents?.find(
                            (d) => d.slot === slot.id,
                        );
                        return (
                            <div
                                key={slot.id}
                                className="flex items-center justify-between gap-4 border-b pb-3 last:border-0"
                            >
                                <div>
                                    <p className="text-sm font-medium">{slot.label}</p>
                                    <p className="text-xs text-gray-500">
                                        {doc?.document_id
                                            ? "Document uploaded"
                                            : "Not uploaded"}
                                    </p>
                                </div>
                                <label className="cursor-pointer rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-50">
                                    Upload
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".pdf,.doc,.docx,.md,.txt"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) handleUpload(slot.id, f);
                                            e.target.value = "";
                                        }}
                                    />
                                </label>
                            </div>
                        );
                    })}
                </div>
            )}

            {customProfiles.length === 0 && (
                <p className="text-sm text-gray-500">No custom profiles yet.</p>
            )}
        </div>
    );
}
